import axios from 'axios';
import { getToken, getRefreshToken, clearLocalStorage, getUserId } from './services/AuthService'; 
import { convertDatesInProps } from './utils/DateUtils';

// Constante para el tamaño de página por defecto
const DEFAULT_PAGE_SIZE = window.location.hostname === 'localhost' ? 20 : 50;

// Crear una instancia de axios con la URL base de la API
const api = axios.create({
    baseURL: window.location.origin + '/api',
    // baseURL: 'http://localhost:5000/api',  // Reemplaza con la URL de tu API
    // baseURL: 'https://kyk-app.org/api',  // Reemplaza con la URL de tu API
    // baseURL: 'https://stg.kyk-app.org/api',  // Reemplaza con la URL de tu API
});

// Función para hacer el login
export const login = async (userName, password) => {
    try {

        const response = await api.post('/auth/login', { userName, password });
        const { token, refreshToken } = response.data;

        // Guardar el token y el refresh token en localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);  // Asegúrate de tener un refreshToken

        return token;
    } catch (error) {
        console.error('Login error:', error);
        logError(error);
        throw new Error(error.response ? error.response.data.message : 'Login failed');
    }
};

export const logout = async () => {
    const token = getToken();  // Obtener el token de acceso (access token)
    const refreshToken = getRefreshToken();  // Obtener el refresh token

    try {
        // Llamamos al endpoint de logout para invalidar el token en el servidor
        await api.post('/auth/logout', { refreshToken }, {
            headers: {
                Authorization: `Bearer ${token}`,  // Enviar el access token en el header Authorization
            },
        });
    } catch (error) {
        logError(error);
        console.error('Logout error:', error);
        // Si ocurre un error al intentar hacer logout, eliminamos los tokens de todos modos
    }finally
    {
        clearLocalStorage();
    }
};

// Función para refrescar el token
export const refreshToken = async () => {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    try {
        const response = await api.post(
            '/auth/refresh',
            JSON.stringify({ refreshToken }),
            {
                headers: {
                    'Content-Type': 'application/json',
                }
            }
        );

        if (response.status === 200) {
            const token = response.data.token;
            const newRefreshToken = response.data.refreshToken;
            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', newRefreshToken);
            return token;
        } else {
            logout();  // <- por si acaso
            window.location.href = '/login';
            return null;
        }
    } catch (error) {
        // Si falla, también redirige al login
        await logout();
        window.location.href = '/login';
        throw error;
    }
};


// Interceptor para manejar errores 401 (token expirado) y hacer refresh del token
api.interceptors.response.use(
    (response) => response,  // Si la respuesta es exitosa, la pasamos
    async (error) => {
        const originalRequest = error.config;

        if (originalRequest.url.includes('/auth/refresh') || originalRequest.url.includes('/auth/login')) {
            // Si es un endpoint de refresh, no hacer nada especial
            return Promise.reject(error);
        }

        // Si recibimos un error 401 y no hemos intentado refrescar el token
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;  // Marcar que hemos intentado refrescar el token

            try {
                const newToken = await refreshToken();  // Intenta obtener un nuevo token
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;  // Agregar el nuevo token a la solicitud
                return axios(originalRequest);  // Reintenta la solicitud original
            } catch (refreshError) {
                logError(refreshError);
                // Si el refresh token falla, realiza logout y redirige al login
                await logout();
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        // Si es error 406, modificar el mensaje para mostrar el texto de respuesta
        if (error.response && error.response.status === 406) {
            const responseData = error.response.data;
            if (responseData && typeof responseData === 'object' && responseData.message) {
                error.message = responseData.message;
            } else {
                error.message = typeof responseData === 'string' ? responseData : error.response.statusText || 'Error 406';
            }
        }

        return Promise.reject(error);
    }
);


// Interceptor para agregar el token a cada solicitud
api.interceptors.request.use(
    (config) => {
        const token = getToken();  // Obtener el token de acceso (access token)
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;  // Añadir el token a la cabecera
        }
        return config;
    },
    (error) => Promise.reject(error)
);

export const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  try {
    const response = await api.get(url, { headers, ...options });  // Pasa los encabezados en la solicitud

    // Auto-conversión de fechas aquí
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const convertedData = convertDatesInProps(response.data, timeZone);

    return convertedData;
  } catch (error) {
    console.error('API error:', error);
    logError(error);
    
    // Para error 406, usar el texto de respuesta directamente
    if (error.response?.status === 406) {
      const responseData = error.response.data;
      if (responseData && typeof responseData === 'object' && responseData.message) {
        throw new Error(responseData.message);
      } else {
        const errorMessage = responseData || error.response.statusText || 'Error 406';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
    }
    
    throw new Error(error.response?.data?.message || 'Request failed');
  }
};

export const fetchBlobWithAuth = async (url, options = {}) => {
  const token = getToken();

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  try {
    const response = await api.get(url, {
      headers,
      ...options,
      responseType: 'blob', // 👈 esto es crucial
    });
    return response.data; // ya es un Blob
  } catch (error) {
    console.error('API error:', error);
    logError(error);
    throw new Error(error.response?.data?.message || 'Request failed');
  }
};


// Funciones para hacer peticiones con autorización
export const fetchFileWithAuth = async (url, options = {}) => {
    const token = getToken();  // Obtener el token del localStorage

    // Verifica si el token existe y añade los encabezados de autorización
    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),  // Si hay un token, añade el header
    };

    try {
        const response = await api.get(url, { headers, ...options });  // Pasa los encabezados en la solicitud
        return response;  // Devuelve la respuesta
    } catch (error) {
        console.error('API error:', error);
        logError(error);
        throw new Error(error.response ? error.response.data.message : 'Request failed');
    }
};

export const fetchWithAuthForFile = async (url, options = {}) => {
    const token = getToken();  // Obtener el token del localStorage

    // Verifica si el token existe y añade los encabezados de autorización
    const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),  // Si hay un token, añade el header
    };

    try {
        // Realizamos la solicitud al servidor para obtener el archivo
        const response = await api.get(url, { 
            headers, 
            responseType: 'arraybuffer',  // Aseguramos que recibimos el archivo como arraybuffer
            ...options 
        });  // Pasa los encabezados en la solicitud

        // Verificamos si la respuesta fue exitosa
        return response;
    } catch (error) {
        console.error('API error:', error);
        logError(error);
        throw new Error(error.response ? error.response.data.message : 'Request failed');
    }
};

// Función para consultar códigos postales
export const getZipCodeInfo = async (zipCode) => {
    try {
        const response = await fetchWithAuth(`/CarrierZipPrefix/has-service/${zipCode}`);
        return response;
    } catch (error) {
        console.error('ZipCode API error:', error);
        throw error;
    }
};

// Función específica para consultas de analytics
export const executeAnalyticsQuery = async (query) => {
    try {
        // Extraer parámetros de paginación y ordenado del query
        const { page, pageSize, sortColumn, sortDirection, ...queryBody } = query;
        
        // Construir URL con parámetros de paginación y ordenado
        const params = new URLSearchParams();
        if (page) params.append('page', page.toString());
        if (pageSize) params.append('pageSize', pageSize.toString());
        if (sortColumn) params.append('sortColumn', sortColumn);
        if (sortDirection) params.append('sortDirection', sortDirection);
        
        const url = `/analytics/query${params.toString() ? '?' + params.toString() : ''}`;
        
        const response = await api.post(url, queryBody);
        return response.data;
    } catch (error) {
        console.error('Analytics API error:', error);
        logError(error);
        throw new Error(error.response?.data?.message || 'Analytics query failed');
    }
};

// Funciones para metadatos de analytics
export const getAnalyticsTables = () => fetchWithAuth('/analytics/tables');
export const getAnalyticsTableColumns = (tableName) => fetchWithAuth(`/analytics/tables/${tableName}/columns`);
export const getAvailableDateColumns = () => fetchWithAuth('/analytics/date-columns');

// Funciones para filtros de productos
export const getFurnitureModelsByType = (typeId) => {
  const url = typeId ? `/furnituremodel/type/${typeId}` : '/furnituremodel/type/';
  return fetchWithAuth(`${url}?includeDisabled=false`);
};
export const getModelColorsByType = (typeId) => {
  const url = typeId ? `/modelcolor/model/${typeId}` : '/modelcolor/model/';
  return fetchWithAuth(`${url}?includeDisabled=false`);
};
export const getModelSizesByType = (typeId) => {
  const url = typeId ? `/modelsize/model/${typeId}` : '/modelsize/model/';
  return fetchWithAuth(`${url}?includeDisabled=false`);
};



export const fetchPdfFile = async (url, options = {}) => {
    const token = getToken();
    const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    try {
        const response = await api.get(url, { 
            headers, 
            responseType: 'arraybuffer',
            ...options 
        });

        if (response.status !== 200) {
            throw new Error(`Error al descargar el archivo: ${response.statusText}`);
        }

        const fileBlob = new Blob([response.data], { type: 'application/pdf' });

        if (fileBlob.type === "application/pdf") {
            return fileBlob;
        } else {
            throw new Error('El archivo no es un PDF válido.');
        }
    } catch (error) {
        logError(error);
        console.error('Error en la solicitud para obtener el archivo:', error);
        throw new Error(error.message || 'Error desconocido');
    }
};

export const logEvent = (evt) =>
  api.post('/Event', evt);

// **Funciones de Clientes y demás**
export const getClients = (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
    // Usamos la paginación para solicitar la página específica de clientes
    return fetchWithAuth(`/client?page=${page}&pageSize=${pageSize}`);
}

export const getClientById = (id) => fetchWithAuth(`/client/${id}`);

// **Funciones de CarrierZipPrefix**
export const getCarrierZipPrefixes = (includeDisabled = false, page = 1, pageSize = DEFAULT_PAGE_SIZE, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({ 
        includeDisabled: includeDisabled.toString(),
        page: page.toString(),
        pageSize: pageSize.toString()
    });
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    return fetchWithAuth(`/CarrierZipPrefix?${params.toString()}`);
};
export const getCarrierZipPrefixById = (id) => fetchWithAuth(`/CarrierZipPrefix/${id}`);
export const getCarrierZipPrefixesByCarrier = (carrierId) => fetchWithAuth(`/CarrierZipPrefix/carrier/${carrierId}`);
export const getBestCarrierForZipCode = (zipCode) => fetchWithAuth(`/CarrierZipPrefix/best-carrier/${zipCode}`);
export const createCarrierZipPrefix = (data) => {
    const token = getToken();
    return api.post('/CarrierZipPrefix', data, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
    });
};
export const updateCarrierZipPrefix = (id, data) => {
    const token = getToken();
    return api.put(`/CarrierZipPrefix/${id}`, data, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
    });
};
export const deleteCarrierZipPrefix = (id) => api.delete(`/CarrierZipPrefix/${id}`);
export const patchCarrierZipPrefix = (id, data) => {
    const token = getToken();
    return api.patch(`/CarrierZipPrefix/${id}`, data, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
    });
};

// **Funciones de Favoritos**
export const getFavorites = () => fetchWithAuth('/ProductFavorite');
export const getFavoriteById = (id) => fetchWithAuth(`/ProductFavorite/${id}`);
export const createFavorite = (favorite) => api.post('/ProductFavorite', favorite);
export const updateFavorite = (id, favorite) => api.put(`/ProductFavorite/${id}`, favorite);
export const deleteFavorite = (id) => api.delete(`/ProductFavorite/${id}`);

// **Funciones de ProductVariant**
export const getProductVariants = (includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({ includeDisabled: includeDisabled.toString() });
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    return fetchWithAuth(`/ProductVariant?${params.toString()}`);
};
export const getProductVariantById = (id) => fetchWithAuth(`/ProductVariant/${id}`);
export const getProductVariantsByTypeId = (typeId, includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({ includeDisabled: includeDisabled.toString() });
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    return fetchWithAuth(`/ProductVariant/type/${typeId}?${params.toString()}`);
};
export const createProductVariant = (variant) => api.post('/ProductVariant', variant);
export const updateProductVariant = (id, variant) => api.put(`/ProductVariant/${id}`, variant);
export const patchProductVariant = (id, variant) => api.patch(`/ProductVariant/${id}`, variant);
export const deleteProductVariant = (id) => api.delete(`/ProductVariant/${id}`);

export const createClient = (client) => {
  // Si el id es null, eliminamos la propiedad para no enviarla
  if (client.id === null) {
    const { id, ...clientWithoutId } = client;
    return api.post('/client', clientWithoutId);
  }
  return api.post('/client', client);
};
export const updateClient = (id, client) => api.put(`/client/${id}`, client);
export const patchClient = (id, client) => api.patch(`/client/${id}`, client);
export const deleteClient = (id) => api.delete(`/client/${id}`);
export const searchClients = (clientSearchReq, page = 1, pageSize = DEFAULT_PAGE_SIZE, includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        includeDisabled: includeDisabled.toString()
    });
    
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    
    return api.post(`/client/search?${params.toString()}`, clientSearchReq);
};

// **Funciones de Direcciones de Cliente**
export const getClientAddresses = () => fetchWithAuth('/clientAddress');  // Obtener todas las direcciones de clientes
export const getClientAddressById = (id) => fetchWithAuth(`/clientAddress/${id}`);  // Obtener una dirección de cliente por ID
export const createClientAddress = (clientAddress) => api.post('/clientAddress', clientAddress);  // Crear una nueva dirección
export const updateClientAddress = (id, clientAddress) => api.put(`/clientAddress/${id}`, clientAddress);  // Actualizar una dirección
export const deleteClientAddress = (id) => api.delete(`/clientAddress/${id}`);  // Eliminar una dirección

export const getOrders = (
  filters,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  orderBy = null,
  desc = false
) => {
  const {
    orderDateFrom,
    orderDateTo,
    deliveryDateFrom,
    deliveryDateTo,
    salesRepId,
    carrierId,
    incidentTypeId,
    incidentActionId,
    clientId,
    status,
    hasSpecialProducts,
    zipCode,
    city,
    productCategoryId,
    clientOriginId,
    paymentMethodId,
    isImportant
  } = filters;

  const params = new URLSearchParams();

  if (orderDateFrom) params.append('orderDateFrom', orderDateFrom);
  if (orderDateTo) params.append('orderDateTo', orderDateTo);
  if (deliveryDateFrom) params.append('deliveryDateFrom', deliveryDateFrom);
  if (deliveryDateTo) params.append('deliveryDateTo', deliveryDateTo);
  if (salesRepId) {
    if (Array.isArray(salesRepId)) {
      salesRepId.forEach(id => params.append('salesRepId', id));
    } else {
      params.append('salesRepId', salesRepId);
    }
  }
  if (carrierId) {
    if (Array.isArray(carrierId)) {
      carrierId.forEach(id => params.append('carrierId', id));
    } else {
      params.append('carrierId', carrierId);
    }
  }
  if (incidentTypeId) params.append('incidentTypeId', incidentTypeId);
  if (incidentActionId) params.append('incidentActionId', incidentActionId);
  if (clientId) params.append('clientId', clientId);
  if (status) params.append('status', status);
  if (hasSpecialProducts !== null) params.append('hasSpecialProducts', hasSpecialProducts);
  if (zipCode) {
    if (Array.isArray(zipCode)) {
      zipCode.forEach(code => params.append('zipCode', code));
    } else {
      params.append('zipCode', zipCode);
    }
  }
  if (city) params.append('city', city);
  if (productCategoryId) params.append('categoryId', productCategoryId);
  if (clientOriginId) params.append('clientOriginId', clientOriginId);
  if (paymentMethodId) params.append('paymentMethodId', paymentMethodId);
  if (isImportant !== null) params.append('isImportant', isImportant);

  // Agregar includeItems=true si estamos en rutas que necesitan los productos
  if (window.location.pathname === '/ordersByCarrier' || window.location.pathname === '/orders-map') {
    params.append('includeItems', 'true');
  }

  params.append('page', page);
  params.append('pageSize', pageSize);

  if (orderBy) {
    params.append('orderBy', orderBy);
    params.append('desc', desc); // false se enviará como "false"
  }

  return fetchWithAuth(`/order?${params.toString()}`);
};
  
export const getOrderById = (id) => fetchWithAuth(`/order/${id}`);  // Obtener un pedido por ID
export const getOrderAndRelatedById = (id) => fetchWithAuth(`/order/${id}/related`);  // Obtener un pedido por ID
export const createOrder = (order) => {
  // Separar productos especiales de productos normales de Items
  const specialProducts = order.Items?.filter(item => item.IsSpecial) || [];
  const normalProducts = order.Items?.filter(item => !item.IsSpecial) || [];
  
  const orderData = {
    ...order,
    Items: normalProducts,
    specialProducts: specialProducts.map(item => ({
      description: item.Description,
      price: item.UnitPrice,
      quantity: item.Quantity,
      stateValueId: 1,
      productTypeId: item.ProductTypeId || null
    }))
  };
  
  return api.post('/order', orderData);
};
export const updateOrder = (id, order) => api.put(`/order/${id}`, order);  // Actualizar un pedido
export const deleteOrder = (id) => api.delete(`/order/${id}`);  // Eliminar un pedido
export const getOrdersByDateAndCarrier = (date, carrierID, page = 1, pageSize = DEFAULT_PAGE_SIZE, zipCode = null) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  
  if (zipCode && zipCode.length > 0) {
    zipCode.forEach(code => params.append('zipCode', code));
  }
  
  return fetchWithAuth(`/order/carrier/${carrierID}/day/${date}?${params.toString()}`);
};
export const getFacturaPdf = (id) => fetchFileWithAuth(`/order/${id}/pdf/factura`, { method: 'GET' });  // Obtener el PDF de la factura
export const getAlbaranPdf = (id) => fetchFileWithAuth(`/order/${id}/pdf/albaran`, { method: 'GET' });  // Obtener el PDF del albarán
export const getSignedAlbaranPdf = (id) => fetchFileWithAuth(`/order/${id}/pdf/albaran-signed`, { method: 'GET' });  // Obtener el PDF del albarán firmado
export const getResumenPdf = (orderIds) => {
  const params = orderIds.map(id => `ids=${id}`).join('&');
  return fetchFileWithAuth(`/order/pdf/resumen?${params}`, { method: 'GET' });
};
export const getOrderPhotos = (id) => fetchWithAuth(`/order/${id}/photos`);
export const patchOrders = (orderIds, { carrierId = null, deliveryDate = null, timeSlotId = null, clientOriginId = null, paymentMethodId = null }) =>
    api.patch('/order/batch', {
      orderIds,
      carrierId,
      deliveryDate,
      timeSlotId,
      clientOriginId,
      paymentMethodId,
    });
export const getBulkAlbaranes = (ids) =>
  fetchBlobWithAuth(`/order/pdf/albaranes?${ids.map(id => `ids=${id}`).join('&')}`);

// Obtener un único PDF con todos los albaranes combinados
export const getCombinedAlbaranesPdf = (ids) =>
  fetchFileWithAuth(`/order/pdf/combined-albaranes?${ids.map(id => `ids=${id}`).join('&')}`, { method: 'GET' });

// Funciones para gestionar roles de usuarios
// Obtener todos los usuarios
export const getUsers = async (includeDisabled = false, orderBy = '', orderDesc = false) => {
    try {
        const params = new URLSearchParams({ includeDisabled: includeDisabled.toString() });
        if (orderBy) {
            params.append('orderBy', orderBy);
            params.append('orderDesc', orderDesc.toString());
        }
        const response = await api.get(`/user?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
};

export const getUsersSalesRepsNotAssigned = async () => {
    try {
        const response = await api.get('/user/sales-reps/not-assigned?disabled=false'); // Endpoint que trae los usuarios no asignados
        return response.data;
    } catch (error) {
        console.error('Error fetching users not assigned:', error);
        throw error;
    }
};

export const getUsersCarriersNotAssigned = async () => {
    try {
        const response = await api.get('/user/carriers/not-assigned?disabled=false'); // Endpoint que trae los usuarios no asignados
        return response.data;
    } catch (error) {
        console.error('Error fetching users not assigned:', error);
        throw error;
    }
};

// Crear un nuevo usuario
export const createUser = async (user) => {
    try {
        const response = await api.post('/user', user); // Suponiendo que el endpoint sea /user
        return response.data;
    } catch (error) {
        console.error('Error creating user:', error);
        throw error;
    }
};

export const createUserIncludingSalesRep = (body) =>
  api.post('/user/include-sales-rep', body);

// Editar un usuario
export const updateUser = async (id, user) => {
    try {
        const response = await api.put(`/user/${id}`, user); // Suponiendo que el endpoint sea /user/{id}
        return response.data;
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
};

export const patchUser = async (id, user) => {
    try {
        const response = await api.patch(`/user/${id}`, user);
        return response.data;
    } catch (error) {
        console.error('Error patching user:', error);
        throw error;
    }
};

// Eliminar un usuario
export const deleteUser = async (id) => {
    try {
        await api.delete(`/user/${id}`); // Suponiendo que el endpoint sea /user/{id}
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};

// Editar un usuario
export const changePassword = async (id, user) => {
    try {
        const response = await api.put(`/user/${id}/password`, user);
        return response.data;
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
};

// Obtener roles
export const getRoles = async () => {
    try {
        const response = await api.get('/role?disabled=false');  // Suponiendo que la ruta es /role
        return response.data;
    } catch (error) {
        console.error('Error fetching roles:', error);
        throw error;
    }
};

// Obtener un usuario por ID
export const getUserById = async (id) => {
    try {
        const response = await api.get(`/user/${id}`); // Suponiendo que el endpoint para obtener un usuario es /user/{id}
        return response.data;
    } catch (error) {
        console.error('Error fetching user by id:', error);
        throw error;
    }
};

export const toggleUserStatus = async (userId, isDisabled) => {
    const response = await api.put(`/user/status/${userId}`, isDisabled, {
        headers: {
            'Content-Type': 'application/json'
        }
    });
    return response.data;
};

export const unblockUser = async (userId) => {
    const response = await api.put(`/user/unblock/${userId}`, {
        headers: {
            'Content-Type': 'application/json'
        }
    });
    return response.data;
};

// **Nueva función para buscar clientes por DNI**
export const searchClientsByDni = async (dni) => {
    try {
        const response = await api.get(`/client/dni?dni=${dni}`);
        return response.data;  // Devuelve la lista de clientes que coinciden con el DNI
    } catch (error) {
        console.error('Error fetching clients by DNI:', error);
        throw error;
    }
};

// **Funciones de ProductCategory**
export const getProductCategories = async (includeDisabled = false) => {
    try {
        const response = await api.get(`/ProductCategory?includeDisabled=${includeDisabled}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product categories:', error);
        throw error;
    }
};

export const getProductCategoryById = async (id) => {
    try {
        const response = await api.get(`/ProductCategory/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product category by id:', error);
        throw error;
    }
};

export const createProductCategory = async (productCategory) => {
    try {
        const response = await api.post('/ProductCategory', productCategory);
        return response.data;
    } catch (error) {
        console.error('Error creating product category:', error);
        throw error;
    }
};

export const updateProductCategory = async (id, productCategory) => {
    try {
        const response = await api.put(`/ProductCategory/${id}`, productCategory);
        return response.data;
    } catch (error) {
        console.error('Error updating product category:', error);
        throw error;
    }
};

export const deleteProductCategory = async (id) => {
    try {
        await api.delete(`/ProductCategory/${id}`);
    } catch (error) {
        console.error('Error deleting product category:', error);
        throw error;
    }
};

// Función para obtener todos los tipos de muebles
export const getFurnitureTypes = async (includeDisabled = false) => {
    try {
        const response = await api.get(`/FurnitureType?includeDisabled=${includeDisabled}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching furniture types:', error);
        throw error;
    }
};

export const getFurnitureTypesByCategory = async (categoryId, includeDisabled = false) => {
    try {
        const response = await api.get(`/FurnitureType/categoryId/${categoryId}?includeDisabled=${includeDisabled}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching furniture types by category:', error);
        throw error;
    }
};

// Función para obtener un tipo de mueble por ID
export const getFurnitureTypeById = async (id) => {
    try {
        const response = await api.get(`/FurnitureType/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching furniture type by id:', error);
        throw error;
    }
};

// Función para crear un nuevo tipo de mueble
export const createFurnitureType = async (furnitureType) => {
    try {
        const response = await api.post('/FurnitureType', furnitureType);
        return response.data;
    } catch (error) {
        console.error('Error creating furniture type:', error);
        throw error;
    }
};

// Función para actualizar un tipo de mueble
export const updateFurnitureType = async (id, furnitureType) => {
    try {
        const response = await api.put(`/FurnitureType/${id}`, furnitureType);
        return response.data;
    } catch (error) {
        console.error('Error updating furniture type:', error);
        throw error;
    }
};

// Función para eliminar un tipo de mueble
export const deleteFurnitureType = async (id) => {
    try {
        await api.delete(`/FurnitureType/${id}`);
    } catch (error) {
        console.error('Error deleting furniture type:', error);
        throw error;
    }
};

// Functions for FurnitureModel
export const getFurnitureModels = () => fetchWithAuth('/furnituremodel?includeDisabled=false');
export const getFurnitureModelById = (id) => fetchWithAuth(`/furnituremodel/${id}`);
export const createFurnitureModel = (furnitureModel) => api.post('/furnituremodel', furnitureModel);
export const updateFurnitureModel = (id, furnitureModel) => api.put(`/furnituremodel/${id}`, furnitureModel);
export const deleteFurnitureModel = (id) => api.delete(`/furnituremodel/${id}`);
export const getFurnitureModelsByTypeId = (typeId, includeDisabled = false) => fetchWithAuth(`/furnituremodel/type/${typeId}?includeDisabled=${includeDisabled}`);

// Functions for ModelColor
export const getModelColors = (modelId) => fetchWithAuth(`/modelcolor?modelId=${modelId}&includeDisabled=false`);
export const getModelColorById = (id) => fetchWithAuth(`/modelcolor/${id}`);
export const createModelColor = (modelColor) => api.post('/modelcolor', modelColor);
export const updateModelColor = (id, modelColor) => api.put(`/modelcolor/${id}`, modelColor);
export const deleteModelColor = (id) => api.delete(`/modelcolor/${id}`);
export const getModelColorsByModelId = (modelId, includeDisabled = false) => fetchWithAuth(`/modelcolor/model/${modelId}?includeDisabled=${includeDisabled}`);
export const getAvailableModelColors = (typeId, modelId, sizeId, includeDisabled = false) => fetchWithAuth(`/modelcolor/available/${typeId}/${modelId}/${sizeId}?includeDisabled=${includeDisabled}`);

// Functions for ModelSize
export const getModelSizes = (modelId) => fetchWithAuth(`/modelsize?modelId=${modelId}&includeDisabled=false`);
export const getModelSizeById = (id) => fetchWithAuth(`/modelsize/${id}`);
export const createModelSize = (modelSize) => api.post('/modelsize', modelSize);
export const updateModelSize = (id, modelSize) => api.put(`/modelsize/${id}`, modelSize);
export const deleteModelSize = (id) => api.delete(`/modelsize/${id}`);
export const getModelSizesByModelId = (modelId, includeDisabled = false) => fetchWithAuth(`/modelsize/model/${modelId}?includeDisabled=${includeDisabled}`);
export const getAvailableModelSizes = (typeId, modelId, colorId, includeDisabled = false) => fetchWithAuth(`/modelsize/available/${typeId}/${modelId}/${colorId}?includeDisabled=${includeDisabled}`);

// Functions for SalesRep
export const getSalesReps = (includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({ includeDisabled: includeDisabled.toString() });
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    return fetchWithAuth(`/salesrep?${params.toString()}`);
};

export const getSalesRepsWithLeadCount = () => {
    return fetchWithAuth('/salesrep/with-lead-count?includeDisabled=false');
};
export const getSalesRepById = (id) => fetchWithAuth(`/salesrep/${id}`);
export const createSalesRep = (salesRep) => api.post('/salesrep', salesRep);
export const updateSalesRep = (id, salesRep) => api.put(`/salesrep/${id}`, salesRep);
export const patchSalesRep = (id, salesRep) => api.patch(`/salesrep/${id}`, salesRep);
export const deleteSalesRep = (id) => api.delete(`/salesrep/${id}`);
export const getCommissionReport = (salesRepId, from, to, orderBy = '', orderDesc = false) => {
  const params = new URLSearchParams({
    dateFrom: from,
    dateTo: to
  });
  if (orderBy) {
    params.append('orderBy', orderBy);
    params.append('orderDesc', orderDesc.toString());
  }
  return fetchWithAuth(`/salesrep/${salesRepId}/commission?${params.toString()}`);
};


// Functions for Carrier
export const getCarriers = (includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({ includeDisabled: includeDisabled.toString() });
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    return fetchWithAuth(`/carrier?${params.toString()}`);
};
export const getCarrierById = (id) => fetchWithAuth(`/carrier/${id}`);
export const createCarrier = (carrier) => api.post('/carrier', carrier);
export const updateCarrier = (id, carrier) => api.put(`/carrier/${id}`, carrier);
export const patchCarrier = (id, carrier) => api.patch(`/carrier/${id}`, carrier);
export const deleteCarrier = (id) => api.delete(`/carrier/${id}`);

// Functions for Product Price
export const getProductPrices = (page = 1, pageSize = DEFAULT_PAGE_SIZE, includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        includeDisabled: includeDisabled.toString()
    });
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    return fetchWithAuth(`/productPrice?${params.toString()}`);
};
export const getProductPriceById = (id) => fetchWithAuth(`/productPrice/${id}`);  // Obtener precio por ID
export const createProductPrice = (productPrice) => api.post('/productPrice', productPrice);  // Crear un nuevo precio
export const updateProductPrice = (id, productPrice) => api.put(`/productPrice/${id}`, productPrice);  // Actualizar un precio
export const patchProductPrice = (id, productPrice) => api.patch(`/productPrice/${id}`, productPrice);  // Actualización parcial de un precio
export const deleteProductPrice = (id) => api.delete(`/productPrice/${id}`);  // Eliminar un precio
export const updateProductPicture = (id, pictureBase64) => {
  const payload = pictureBase64 === null 
    ? { deletePicture: true } 
    : { pictureBase64 };
  return api.patch(`/productPrice/${id}`, payload);
};
export const getProductPrice = (params) => {
    const { typeId, modelId, colorId, sizeId } = params;
    return fetchWithAuth(`/productPrice/by-ids?typeId=${typeId}&modelId=${modelId}&colorId=${colorId}&sizeId=${sizeId}`);
  };

  // Función para obtener los precios de los productos, con typeId y modelId opcionales
  export const GetProductPricesByTypeIdAndModelId = (typeId, modelId, page = 1, itemsPerPage = DEFAULT_PAGE_SIZE, includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({
        page: page.toString(),
        itemsPerPage: itemsPerPage.toString(),
        includeDisabled: includeDisabled.toString()
    });
    
    if (modelId) {
        params.append('modelId', modelId);
    }
    
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    
    return fetchWithAuth(`/productPrice/typeId/${typeId}?${params.toString()}`);
  };

  // Función para obtener los precios de los productos por categoría
  export const GetProductPricesByCategoryId = (categoryId, typeId, modelId, page = 1, itemsPerPage = DEFAULT_PAGE_SIZE, includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({
        page: page.toString(),
        itemsPerPage: itemsPerPage.toString(),
        includeDisabled: includeDisabled.toString()
    });
    
    if (typeId) {
        params.append('typeId', typeId);
    }
    
    if (modelId) {
        params.append('modelId', modelId);
    }
    
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    
    return fetchWithAuth(`/productPrice/categoryId/${categoryId}?${params.toString()}`);
  };
  

export const getOrderItemsByOrderId = (orderId) => fetchWithAuth(`/orderItem/by-order/${orderId}`);
export const getOrderItemsByCarrierId = (
  carrierId,
  deliveryDateFrom,
  deliveryDateTo,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  group = true,
  orderBy = 'productPrice.type.typeName',
  orderDesc = true,
  zipCodes = [],
  categoryId = null,
  typeId = null,
  modelId = null,
  colorId = null,
  sizeId = null
) => {
  const params = new URLSearchParams();

  if (deliveryDateFrom) params.append('deliveryDateFrom', deliveryDateFrom);
  if (deliveryDateTo)   params.append('deliveryDateTo', deliveryDateTo);

  params.append('group', group);

  params.append('orderBy', orderBy);
  params.append('orderDesc', orderDesc.toString());

  if (group) {
    params.append('page', page);
    params.append('pageSize', pageSize);
  } else {
    params.append('page', 1);
    params.append('pageSize', 1000000);
  }

  if (zipCodes && zipCodes.length > 0) {
    zipCodes.forEach(code => params.append('zipCode', code));
  }
  if (categoryId) params.append('categoryId', categoryId);
  if (typeId)     params.append('typeId', typeId);
  if (modelId)    params.append('modelId', modelId);
  if (colorId)    params.append('colorId', colorId);
  if (sizeId)     params.append('sizeId', sizeId);

  return fetchWithAuth(`/orderItem/carrier/${carrierId}?${params.toString()}`);
};

export const updateQuantityLoaded = (orderItemId, quantityLoaded) =>
  api.patch(
    `/orderItem/${orderItemId}/quantity-loaded/${quantityLoaded}`
  );

export const createProductWithNames = (productDetails) => api.post('/productPrice/by-names', productDetails);

// Función utilitaria para cargar todos los listados necesarios para productos
export const loadAllProductDropdowns = async (product = null) => {
    try {
        const promises = [];
        
        // Cargar categorías
        promises.push(getProductCategories());
        
        // Cargar tipos (por categoría si se especifica, o todos)
        if (product?.category) {
            promises.push(getFurnitureTypesByCategory(product.category.id));
        } else {
            promises.push(getFurnitureTypes());
        }
        
        const [categories, types] = await Promise.all(promises);
        
        let models = [];
        let colors = [];
        let sizes = [];
        
        // Si hay un producto específico, cargar modelos, colores y tamaños
        if (product?.type) {
            const selectedType = types.find(t => t.typeName === product.type.typeName || t.id === product.type.id);
            if (selectedType) {
                models = await getFurnitureModelsByTypeId(selectedType.id);
                
                if (product?.model) {
                    const selectedModel = models.find(m => m.modelName === product.model.modelName || m.id === product.model.id);
                    if (selectedModel) {
                        [colors, sizes] = await Promise.all([
                            getModelColorsByModelId(selectedModel.id),
                            getModelSizesByModelId(selectedModel.id)
                        ]);
                    }
                }
            }
        }
        
        return {
            categories,
            types,
            models,
            colors,
            sizes
        };
    } catch (error) {
        logError(error);
        throw error;
    }
};

// **Funciones de Constants**
export const getIncidentTypes = () => fetchWithAuth('/constant/incident-types?disabled=false');
export const getIncidentActions = () => fetchWithAuth('/constant/incident-actions?disabled=false');
export const getTimeSlots = () => fetchWithAuth('/constant/time-slots?disabled=false');
export const getEventTypes = () => fetchWithAuth('/constant/event-types?disabled=false');
export const getRegistryLeadTypes = () => fetchWithAuth('/constant/registry-lead-type?disabled=false');
export const getLeadStatuses = () => fetchWithAuth('/constant/lead-statuses?disabled=false');
export const getLeadSubStatuses = (statusId = null) => {
    const baseUrl = statusId ? `/constant/lead-sub-status?statusId=${statusId}` : '/constant/lead-sub-status';
    const separator = statusId ? '&' : '?';
    return fetchWithAuth(`${baseUrl}${separator}disabled=false`);
};
export const getSpecialProductStatus = () => fetchWithAuth('/constant/special-product-status?disabled=false');
export const getTelegramEntityTypes = () => fetchWithAuth('/constant/telegram-entity-types?disabled=false');
export const getClientOrigins = () => fetchWithAuth('/constant/client-origins?disabled=false');
export const getPaymentMethods = () => fetchWithAuth('/constant/payment-methods?disabled=false');
export const getLeadSources = () => fetchWithAuth('/constant/lead-sources?disabled=false');

export const handleSaveEditOrder = async (orderId, modifiedItems, mileage, setSuccessMessage, prepaidAmount) => {
    try {
      if (!modifiedItems) {
        alert("No hay cambios para guardar.");
        return;
      }
      
      const items = modifiedItems.items || modifiedItems;
      const specialProducts = modifiedItems.specialProducts || [];
      
      if (items.length === 0 && specialProducts.length === 0) {
        alert("No hay cambios para guardar.");
        return;
      }
  
      // Normalizamos los items para enviarlos correctamente
      const normalizedItems = items.map((item) => ({
        ...item,
        id: String(item.id).startsWith('new-') ? 0 : item.id, 
        orderId, 
        productTypeId: item.productType?.id || item.productTypeId,
        modelId: item.model?.id || item.modelId,
        colorId: item.color?.id || item.colorId,
        sizeId: item.size?.id || item.sizeId,
        // Asegurar que productPrice incluye toda la información necesaria
        productPrice: {
          ...item.productPrice,
          category: item.productPrice?.category || item.productPrice?.type?.category,
          categoryId: item.productPrice?.categoryId || item.productPrice?.type?.categoryId
        }
      }));
      
      // Normalizamos los productos especiales y eliminamos updatedAt
      const normalizedSpecialProducts = specialProducts.map(item => {
        // Crear una copia del item sin el campo updatedAt
        const { updatedAt, ...itemWithoutUpdatedAt } = item;
        return {
          ...itemWithoutUpdatedAt,
          id: typeof item.id === 'string' || item.id < 0 ? 0 : item.id,
          orderId,
          // Asegurarse de que price y quantity sean números
          price: Number(item.price || 0),
          quantity: Number(item.quantity || 1)
        };
      });
  
      // Preparar la estructura del pedido a actualizar
      const updatedOrder = {
        id: orderId,
        ModifiedItems: normalizedItems,
        ModifiedSpecialProducts: normalizedSpecialProducts,
      };
      
      // Solo incluir mileage si ha sido editado y no está vacío
      if (mileage !== null && mileage !== undefined && !(typeof mileage === 'object' && Object.keys(mileage).length === 0)) {
        updatedOrder.mileage = mileage;
      }

      // Incluir alreadyPaid si el usuario ha especificado un valor válido
      const parsedPrepaid = parseFloat(prepaidAmount);
      if (prepaidAmount !== null && prepaidAmount !== undefined && !isNaN(parsedPrepaid)) {
        updatedOrder.alreadyPaid = parsedPrepaid;
      }
  
      // Llamar a la API para guardar los cambios
      await updateOrder(orderId, updatedOrder);
  
      // Mostrar el "alert" emulado y luego desaparecerlo
      setSuccessMessage("Pedido editado con éxito.");
  
      // Limpiar el mensaje después de 3 segundos
      setTimeout(() => setSuccessMessage(""), 3000);
  
    } catch (error) {
      console.error("Error al guardar los cambios:", error);
      logError(error);
      alert("Hubo un problema al guardar los cambios. Inténtelo de nuevo.");
    }
  };

  // Comisiones de Transportista
export const getCarrierCommissions = (orderBy = '', orderDesc = false) => {
  const params = new URLSearchParams();
  if (orderBy) {
    params.append('orderBy', orderBy);
    params.append('orderDesc', orderDesc.toString());
  }
  return fetchWithAuth(`/carrierCommissions?${params.toString()}`);
};

export const getCarrierCommissionById = (id) =>
  fetchWithAuth(`/carrierCommissions/${id}`);

export const createCarrierCommission = (commission) =>
  api.post('/carrierCommissions', commission);

export const updateCarrierCommission = (id, commission) =>
  api.put(`/carrierCommissions/${id}`, commission);

export const deleteCarrierCommission = (id) =>
  api.delete(`/carrierCommissions/${id}`);

// Precios de Comisiones por Transportista
export const getCarrierCommissionPrices = (carrierId, orderBy = '', orderDesc = false) => {
  const params = new URLSearchParams();
  if (orderBy) {
    params.append('orderBy', orderBy);
    params.append('orderDesc', orderDesc.toString());
  }
  return fetchWithAuth(`/carrierCommissions/${carrierId}/prices?${params.toString()}`);
};

export const getCarrierCommissionPrice = (carrierId, commissionId) =>
  fetchWithAuth(`/carrierCommissions/${carrierId}/prices/${commissionId}`);

export const setCarrierCommissionPrice = (carrierId, price) =>
  api.post(`/carrierCommissions/${carrierId}/prices`, price);

export const deleteCarrierCommissionPrice = (carrierId, id) =>
  api.delete(`/carrierCommissions/${carrierId}/prices/${id}`);


export const logToApi = (log) => {return api.post('/Log', log);};

export const logError = async (error) => {
    // Para error 406, usar el texto de respuesta como mensaje
    let errorMessage = error.message || 'Error no manejado';
    if (error.response?.status === 406) {
      const responseData = error.response.data;
      if (responseData && typeof responseData === 'object' && responseData.message) {
        errorMessage = responseData.message;
      } else {
        errorMessage = typeof responseData === 'string' ? responseData : error.response.statusText || 'Error 406';
      }
    }

    const log = {
      logLevel: 'Error',
      message: errorMessage,
      exception: error.stack || '',
      userId: null,  // O el ID del usuario si lo tienes en contexto
      source: error.stack ? error.stack.split('\n')[1] : 'Unknown source',  // Intentar obtener la fuente del error
      application: 'FRONT',
      path: window.location.pathname,
      statusCode: error.response?.status || 500,
      ipAddress: '', // Si necesitas la IP, la puedes obtener aquí
    };
  
    try {
      // Intentar enviar el log a la API
      await logToApi(log);
      console.error('Error registrado:', log);
    } catch (apiError) {
      // En caso de que el registro del error falle, lo mostramos en consola
      console.error('Error al registrar el log:', apiError);
    }
  };
  
  // api.js
  export const getLogs = (
    {
      id,
      logLevel,
      messageContains,
      timestampFrom,
      timestampTo,
      exceptionContains,
      userId,
      sourceContains,
      applicationContains,
      httpMethod,
      pathContains,
      queryStringContains,
      controllerContains,
      actionContains,
      requestBodyContains,
      statusCode,
      durationMsFrom,
      durationMsTo,
      ipAddressContains,
      includeSensitive,
      password,
      orderBy,
      orderDesc
    },
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE
  ) => {
    const params = new URLSearchParams();
    if (id != null)               params.append('id', id);
    if (logLevel)                 params.append('logLevel', logLevel);
    if (messageContains)          params.append('messageContains', messageContains);
    if (timestampFrom)            params.append('timestampFrom', timestampFrom);
    if (timestampTo)              params.append('timestampTo', timestampTo);
    if (exceptionContains)        params.append('exceptionContains', exceptionContains);
    if (userId != null)           params.append('userId', userId);
    if (sourceContains)           params.append('sourceContains', sourceContains);
    if (applicationContains)      params.append('applicationContains', applicationContains);
    if (httpMethod)               params.append('httpMethod', httpMethod);
    if (pathContains)             params.append('pathContains', pathContains);
    if (queryStringContains)      params.append('queryStringContains', queryStringContains);
    if (controllerContains)       params.append('controllerContains', controllerContains);
    if (actionContains)           params.append('actionContains', actionContains);
    if (requestBodyContains)      params.append('requestBodyContains', requestBodyContains);
    if (statusCode != null)       params.append('statusCode', statusCode);
    if (durationMsFrom != null)   params.append('durationMsFrom', durationMsFrom);
    if (durationMsTo != null)     params.append('durationMsTo', durationMsTo);
    if (ipAddressContains)        params.append('ipAddressContains', ipAddressContains);
    if (includeSensitive)         params.append('includeSensitive', includeSensitive);
    if (password)                 params.append('password', password);
    if (orderBy) {
      params.append('orderBy', orderBy);
      params.append('orderDesc', orderDesc.toString());
    }

    params.append('page', page);
    params.append('pageSize', pageSize);

    return fetchWithAuth(`/log?${params.toString()}`);
  };

  export const updateSpecialProductQuantityLoaded = (specialProductId, quantityLoaded) => 
  api.patch(`/orderItem/special/${specialProductId}/quantity-loaded/${quantityLoaded}`);


// Configuración global de interceptores
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Evitar intentar registrar el log si el envío falló anteriormente
    if (error.config?.url === "/Log") {
      return Promise.reject(error); // Si el log ya falló, simplemente rechazamos el error y lo manejamos donde corresponda
    }

    // Para error 406, modificar el mensaje antes de loggear
    let errorMessage = error.message || 'Unknown error';
    if (error.response?.status === 406) {
      const responseData = error.response.data;
      if (responseData && typeof responseData === 'object' && responseData.message) {
        errorMessage = responseData.message;
      } else {
        errorMessage = typeof responseData === 'string' ? responseData : error.response.statusText || 'Error 406';
      }
      error.message = errorMessage;
    }

    const log = {
      logLevel: 'Error',
      message: errorMessage,
      source: 'API Interceptor',
      application: 'FRONT',
      exception: JSON.stringify(error.response?.data || {}),
      httpMethod: error.config?.method?.toUpperCase(),
      path: error.config?.url,
      statusCode: error.response?.status,
      timestamp: new Date().toISOString(),
    };

    try {
      // Intentar enviar el log al servidor
      await logToApi(log);
      console.error('Log de error enviado al servidor:', log);
    } catch (apiError) {
      // Si el envío del log falla, marcamos que no se debe volver a intentar
      console.error('Error al enviar el log al servidor:', apiError);
    }

    return Promise.reject(error); // Rechaza el error para que sea manejado donde corresponda
  }
);

export const getEvents = (filters, page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
  const { eventTypeId, userId, orderBy, orderDesc } = filters;
  const params = new URLSearchParams();

  if (eventTypeId != null) params.append('eventTypeId', eventTypeId);
  if (userId      != null) params.append('userId', userId);
  if (orderBy) {
    params.append('orderBy', orderBy);
    params.append('orderDesc', orderDesc.toString());
  }

  params.append('page', page);
  params.append('pageSize', pageSize);

  return fetchWithAuth(`/Event?${params.toString()}`);
};

export async function getOrderItemsByCarrierWithEvent(
  carrierId,
  deliveryDateFrom,
  deliveryDateTo,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  group = true,
  eventTypeId,
  orderBy = 'productPrice.type.typeName',
  orderDesc = false,
  zipCodes = [],
  categoryId = null,
  typeId = null,
  modelId = null,
  colorId = null,
  sizeId = null
) {
  // 1) Ejecuta la llamada original
  const result = await getOrderItemsByCarrierId(
    carrierId,
    deliveryDateFrom,
    deliveryDateTo,
    page,
    pageSize,
    group,
    orderBy,
    orderDesc,
    zipCodes,
    categoryId,
    typeId,
    modelId,
    colorId,
    sizeId
  );

//   if(group)
//   {
//     return result;
//   }

  // 2) Si va bien, arma el objeto Event
  const evt = {
    EventTypeId: eventTypeId,
    UserId:      getUserId(),
    Entity:      'carrier',
    EntityId:    Number(carrierId),
    Timestamp:   new Date().toISOString(),
    Payload:     JSON.stringify({ deliveryDateFrom, deliveryDateTo, group }),
    IndexRoute:  window.location.pathname
  };

  // 3) Lo envías al POST /api/Event
  await api.post('/Event', evt);

  // 4) Devuelves el resultado original
  return result;
}


// **Funciones de TelegramChannelAssignment**
export const getTelegramChannelAssignments = (includeDisabled = false, orderBy = '', orderDesc = false) => {
    const params = new URLSearchParams({ includeDisabled: includeDisabled.toString() });
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    return fetchWithAuth(`/TelegramChannelAssignment?${params.toString()}`);
};
export const getTelegramChannelAssignmentById = (id) => fetchWithAuth(`/TelegramChannelAssignment/${id}`);
export const getTelegramChannelAssignmentsByEntity = (entityTypeValueId, entityReferenceId) => fetchWithAuth(`/TelegramChannelAssignment/entity/${entityTypeValueId}/${entityReferenceId}`);
export const createTelegramChannelAssignment = (assignment) => api.post('/TelegramChannelAssignment', assignment);
export const updateTelegramChannelAssignment = (id, assignment) => api.patch(`/TelegramChannelAssignment/${id}`, assignment);
export const patchTelegramChannelAssignment = (id, assignment) => api.patch(`/TelegramChannelAssignment/${id}`, assignment);
export const deleteTelegramChannelAssignment = (id) => api.delete(`/TelegramChannelAssignment/${id}`);

// **Funciones de Lead**
export const getLeads = (page = 1, pageSize = DEFAULT_PAGE_SIZE, includeDisabled = false, orderBy = '', orderDesc = false, filters = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        includeDisabled: includeDisabled.toString()
    });
    
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            if (key === 'salesRepId' && Array.isArray(value)) {
                value.forEach(id => params.append('salesRepId', id));
            } else {
                params.append(key, value);
            }
        }
    });
    
    return fetchWithAuth(`/lead?${params.toString()}`);
};
export const getUsersWithLeadRole = (roleId) => fetchWithAuth(`/user?roleId=${roleId}&includeDisabled=false&disabled=false`);
export const getLeadById = (id) => fetchWithAuth(`/lead/${id}`);
export const createLead = (lead) => api.post('/lead', lead);
export const updateLead = (id, lead) => api.put(`/lead/${id}`, lead);
export const patchLead = (id, lead) => api.patch(`/lead/${id}`, lead);
export const deleteLead = (id) => api.delete(`/lead/${id}`);

// **Funciones de LeadRegistry**
export const getLeadRegistries = (leadId) => fetchWithAuth(`/leadRegistry/lead/${leadId}`);
export const createLeadRegistry = (leadRegistry) => api.post('/leadRegistry', leadRegistry);

// **Funciones de SalesRepLogo**
export const getSalesRepLogos = (salesRepId) => fetchWithAuth(`/salesRepLogo/salesrep/${salesRepId}`);
export const getSalesRepLogoById = (id) => fetchWithAuth(`/salesRepLogo/${id}`);
export const createSalesRepLogo = (salesRepLogo) => api.post('/salesRepLogo', salesRepLogo);
export const updateSalesRepLogo = (id, salesRepLogo) => api.put(`/salesRepLogo/${id}`, salesRepLogo);
export const deleteSalesRepLogo = (id) => api.delete(`/salesRepLogo/${id}`);

// **Funciones de LeadAccountComment**
export const getLeadAccountComments = (leadAccountId) => fetchWithAuth(`/leadAccountComment/leadAccount/${leadAccountId}`);
export const createLeadAccountComment = (comment) => api.post('/leadAccountComment', comment);
export const updateLeadAccountComment = (id, comment) => api.put(`/leadAccountComment/${id}`, comment);
export const deleteLeadAccountComment = (id) => api.delete(`/leadAccountComment/${id}`);

export default api;
// Agregar al final del archivo api.js

// **Funciones de SpecialProduct**
export const getSpecialProducts = (page = 1, pageSize = DEFAULT_PAGE_SIZE, filters = {}, orderBy = '', orderDesc = false) => {
  const params = new URLSearchParams();
  
  // Añadir parámetros de paginación
  params.append('page', page);
  params.append('pageSize', pageSize);
  
  // Añadir filtros si existen
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });
  
  // Añadir ordenamiento
  if (orderBy) {
    params.append('orderBy', orderBy);
    params.append('orderDesc', orderDesc.toString());
  }
  
  return fetchWithAuth(`/SpecialProduct?${params.toString()}`);
};

export const getSpecialProductById = (id) => fetchWithAuth(`/SpecialProduct/${id}`);
export const getSpecialProductsByOrderId = (orderId) => fetchWithAuth(`/SpecialProduct/order/${orderId}`);
export const createSpecialProduct = (specialProduct) => api.post('/SpecialProduct', specialProduct);
export const updateSpecialProduct = (id, specialProduct) => api.put(`/SpecialProduct/${id}`, specialProduct);
export const updateSpecialProductState = (id, stateValueId) => api.patch(`/SpecialProduct/${id}/state/${stateValueId}`);
export const deleteSpecialProduct = (id) => api.delete(`/SpecialProduct/${id}`);

// **Funciones de Autocompletado de Dirección**
export const searchAddressAutocomplete = async (q, signal) => {
    const response = await api.get('/ClientAddress/autocomplete', { params: { q }, signal });
    return response.data;
};

// **Funciones de Geolocalización**
export const getOrdersGeolocation = (orderIds) => {
  const params = orderIds.map(id => `orderIds=${id}`).join('&');
  return fetchWithAuth(`/ClientAddress/geolocation/orders?${params}`);
};

// **Funciones de edición rápida de nombres**
export const updateCategoryName = (id, name) => api.patch(`/ProductCategory/${id}`, { categoryName: name });
export const updateTypeName = (id, name) => api.patch(`/FurnitureType/${id}`, { typeName: name });
export const updateModelName = (id, name) => api.patch(`/furnituremodel/${id}`, { modelName: name });
export const updateColorName = (id, name) => api.patch(`/modelcolor/${id}`, { colorName: name });
export const updateSizeName = (id, name) => api.patch(`/modelsize/${id}`, { size: name });

// **Funciones de LeadAccount**
export const getLeadAccounts = (page = 1, pageSize = DEFAULT_PAGE_SIZE, includeDisabled = false, orderBy = '', orderDesc = false, filters = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        includeDisabled: includeDisabled.toString()
    });
    
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, value);
        }
    });
    
    return fetchWithAuth(`/leadAccount?${params.toString()}`);
};
export const getLeadAccountById = (id) => fetchWithAuth(`/leadAccount/${id}`);
export const createLeadAccount = (leadAccount) => api.post('/leadAccount', leadAccount);
export const updateLeadAccount = (id, leadAccount) => api.put(`/leadAccount/${id}`, leadAccount);
export const patchLeadAccount = (id, leadAccount) => api.patch(`/leadAccount/${id}`, leadAccount);
export const deleteLeadAccount = (id) => api.delete(`/leadAccount/${id}`);

// **Funciones de LeadAccountDailyStats**
export const getLeadAccountDailyStats = (page = 1, pageSize = DEFAULT_PAGE_SIZE, includeDisabled = false, orderBy = '', orderDesc = false, filters = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        includeDisabled: includeDisabled.toString()
    });
    
    if (orderBy) {
        params.append('orderBy', orderBy);
        params.append('orderDesc', orderDesc.toString());
    }
    
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, value);
        }
    });
    
    return fetchWithAuth(`/leadAccountDailyStats?${params.toString()}`);
};
export const getLeadAccountDailyStatsById = (id) => fetchWithAuth(`/leadAccountDailyStats/${id}`);
export const createLeadAccountDailyStats = (stats) => api.post('/leadAccountDailyStats', stats);
export const updateLeadAccountDailyStats = (id, stats) => api.put(`/leadAccountDailyStats/${id}`, stats);
export const patchLeadAccountDailyStats = (id, stats) => api.patch(`/leadAccountDailyStats/${id}`, stats);
export const deleteLeadAccountDailyStats = (id) => api.delete(`/leadAccountDailyStats/${id}`);
export const getDailyStats = (date, assignedLeadId = null) => {
    const params = new URLSearchParams();
    if (assignedLeadId) params.append('assignedLeadId', assignedLeadId);
    return fetchWithAuth(`/leadAccountDailyStats/daily/${date}?${params.toString()}`);
};

// **Función para actualizar puntuaciones en lote**
export const updateProductScoresBatch = (scoreUpdateRequest) => {
    return api.patch('/productPrice/batch/score', scoreUpdateRequest);
};